import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import { getCaseProcessingStatusApiV1CasesCaseNumberProcessingStatusGet } from "../../api/generated/sdk.gen";
import { CLIENT_KEYS } from "../../constants/clientKeys";
import { buildApiErrorMessage } from "../../utils/notification";

/** Poll interval while the case's documents are still arriving. */
export const CASE_PROCESSING_ACTIVE_POLL_MS = 10_000;
/**
 * Poll interval while idle. Still polls: the admin's upload can start after the
 * doctor has already opened the case, and that's exactly when they need to know.
 */
export const CASE_PROCESSING_IDLE_POLL_MS = 30_000;

/**
 * Are the case's documents still being uploaded or summarised? (MDRS-508)
 *
 * Covers uploads started by anyone on the case — typically an admin preparing
 * it for the doctor — so the doctor sees the upload and Finalise stays held
 * back until it settles. The backend enforces the same rule on finalise.
 */
export const useCaseProcessingStatus = ({
  case_number,
  enabled = true,
}: {
  case_number: string;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: [CLIENT_KEYS.CASES, "processing-status", case_number],
    enabled: enabled && !!case_number,
    queryFn: async () => {
      const response =
        await getCaseProcessingStatusApiV1CasesCaseNumberProcessingStatusGet({
          client: apiClient,
          path: { case_number },
        });
      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage(
            "Failed to check whether the case is still processing",
            response.error
          )
        );
      }
      return response.data;
    },
    refetchInterval: (query) =>
      query.state.data?.is_processing
        ? CASE_PROCESSING_ACTIVE_POLL_MS
        : CASE_PROCESSING_IDLE_POLL_MS,
  });
